import { useState, useEffect, lazy, Suspense } from "react";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { Card } from "../../../components/ui/card";
import { Label } from "../../../components/ui/label";
import { Checkbox } from "../../../components/ui/checkbox";

import { Link, useNavigate } from "react-router-dom";
import { fetchWalletBalanceService, setLoggedUserDetails } from "../../../state/actions/user";
import { useDispatch } from "react-redux";
import { getDomainConfig, isFrontmAiDomain, isOnshipDomain, isPalCareDev2Domain, isSeaDanceDomain, isSeamedixDomain, isStageOnshipDomain, isVikandDomain } from "../../../utils/DomainsHelper";
import { storeAuthData } from "../../../services/storage/LocalStorage";
import { ImagePath } from "../../../assets/ImagePath";
import { appType } from "../../../utils/config/configHelpers";
import { setInializeSocket } from "../../../state/actions/socket";
import { updateAppHead, uuidGenerator } from "../../../utils/general";
import { getUserDomainList, setDomainState } from "../../../state/actions/domain";
import { setBotWaitLoader } from "../../../state/actions/loader";
import { setClearCacheForSpinnetBot } from "../../../state/actions/bot";
import { setSessionIdInAxios } from "../../../services/userApiServices/axiosInstance";
import { AuthRightPanel } from "../../../components/layout/AuthLayout/AuthRightPanel";
import authFunctions from "../../../services/userApiServices/authFunctions";
import GoogleSignInButton from "../../../components/common/GoogleSignUpButton";
import useIsLaptopScreen from "../../../utils/customHooks/useIsLaptopScreen";
import LoadingModal from "../../../components/ui/Loader/LoadingModal";
import CommonModalPopUp from "../../../components/ui/Modals/ModalPopupCommon";
import { UserServiceClientApi } from "../../../services/api/UserApiServices";
import { showToast } from "../../../components/ui/toaster";


const SeamedixLoginError = lazy(() => import('../../../components/common/Modals/seamedixLoginError'));

const Login = () => {
  const navigate = useNavigate();
  const [isFocused, setIsFocused] = useState({ email: false, password: false });
  const [userData, setUserData] = useState({ email: "", password: "", });

  const [highlightEmail, setHighlightEmail] = useState(false);
  const [errors, setErrors] = useState({ emailError: "", passwordError: "", });

  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [logoImage, setLogoImage] = useState("");
  const [bgImage, setBgImage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const isLaptopScreen = useIsLaptopScreen();

  //handle auth screens according domain
  //=> For onship AppType
  const showAuth = isOnshipDomain || isPalCareDev2Domain

  const dispatch = useDispatch();

  useEffect(() => {
    const rememberMe = localStorage.getItem('rememberMe')
    const userData = JSON.parse(localStorage.getItem('userLoginData'));

    if (rememberMe && userData) {
      setRememberMe(true);
      setUserData({
        email: userData.email,
        password: userData.password,
      });
    }

    const config = getDomainConfig();
    setLogoImage(config.logo);
    setBgImage(config.banner);
    updateAppHead({
      title: config.title,
      favicon: config.favicon,
    });
  }, []);


  const handleChange = (event, type) => {
    const { value } = event.target;
    if (type == "email") {
      setUserData((prevData) => ({ ...prevData, email: value }));
      setErrors((prev) => ({ ...prev, emailError: "" }));
    }
    if (type == "password") {
      setUserData((prevData) => ({ ...prevData, password: value }));
      setErrors((prev) => ({ ...prev, passwordError: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { email, password } = userData;
    let isValid = true;
    const newErrors = {};

    if (email == "") {
      newErrors.emailError = "Email is required";
      isValid = false;
    }

    if (password == "") {
      newErrors.passwordError = "Password is Invalid. Please try again.";
      isValid = false;
    }

    setErrors(newErrors);
    if (!isValid) return;

    try {
      setIsLoading(true);
      let response = await authFunctions.loginUser(email, password, appType);
      if (response && response.success) {
        handleSuccessfulLogin(response, rememberMe, userData);
      }
      else if (
        response?.message === "Missing required parameter SOFTWARE_TOKEN_MFA_CODE"
        || response?.errorCode === "UserNotConfirmedException"
      ) {
        const params = { email: email, appType: appType };
        try {
          const response = await UserServiceClientApi.resendOtp(params);
          if (response.success) {
            showToast("success", "OTP has been sent to your email successfully. Please verify your email.");
            navigate('/verify', {
              state: {
                email,
                password,
              },
            });
          } else {
            showToast("error", response?.message || "Something went wrong.");
          }
        } catch (error) {
          showToast("error", error.message || "An error occurred.");
        }
      }
      else {
        if (isSeamedixDomain) setShowModal(true);
        else setErrors({ emailError: " ", passwordError: response?.message || "Login failed" });
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Login error:", error);
      setErrors({
        emailError: "",
        passwordError: "Login failed. Please try again.",
      });
      setIsLoading(false);
    }
  };


  const handleGoogleSignIn = async (code) => {
    try {
      const params = { code, platform: "web", appType };
      const apiResponse = await authFunctions.doGoogleSignin(params);

      if (apiResponse?.success) {
        await handleSuccessfulLogin(apiResponse);
      } else {
        setErrors({ emailError: " ", passwordError: apiResponse?.message || "Google Sign-in failed" });
      }

      console.log("Backend response:", apiResponse);
    } catch (error) {
      console.error("Backend login error:", error);
    }
  };


  useEffect(() => {
    const handleEnterKey = (e) => {
      if (e.key === "Enter") {
        handleSubmit(e);
      }
    };

    window.addEventListener("keydown", handleEnterKey);
    return () => window.removeEventListener("keydown", handleEnterKey);
  }, [userData, rememberMe]);


  const handleSuccessfulLogin = async (payload, rememberMe, userData) => {
    // Save session + user info
    setSessionIdInAxios(payload.sessionId);
    localStorage.setItem('sessionId', payload.sessionId);
    localStorage.setItem('user', JSON.stringify(payload?.user));

    // Store auth info
    storeAuthData(payload);
    dispatch(setLoggedUserDetails(payload));

    // Handle "Remember Me"
    if (rememberMe) {
      localStorage.setItem('rememberMe', rememberMe);
      localStorage.setItem('userLoginData', JSON.stringify(userData));
    } else {
      localStorage.removeItem('rememberMe');
      localStorage.removeItem('userLoginData');
    }

    // Init app state
    dispatch(setBotWaitLoader(true));
    dispatch(setInializeSocket(true));
    dispatch(setClearCacheForSpinnetBot(true));

    await getUserDomainList();
    dispatch(setDomainState(true));

    // Navigate to main app
    navigate('/app/chats');
    setIsLoading(false);
    // Fetch wallet balance
    await dispatch(fetchWalletBalanceService());
  };

  //======= JWT LOGIN ======>>>>>

  const handleLoginWithJWT = async (token) => {
    const userData = JSON.parse(localStorage.getItem('userLoginData'));
    if (userData) return navigate('/app/chats');


    try {
      setIsLoading(true);

      const params = {
        requestId: uuidGenerator(),
        service: "AuthService",
        method: "SSOSignin",
        payload: { appType, platform: "web", token },
      };

      const response = await authFunctions.loginWithJWT(params);
      const payload = response?.payload;

      if (payload?.success) {
        await handleSuccessfulLogin(payload, rememberMe, userData);
      } else {
        if (isSeamedixDomain) setShowModal(true);
        else setErrors({ emailError: " ", passwordError: response?.error || "JWT login failed" });
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Login error:", error);
      setErrors({ emailError: "", passwordError: "Login failed. Please try again." });
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");
    if (token) {
      handleLoginWithJWT(token);
    }
  }, []);


  return (
    <>
      {/* login page */}
      <section className={"login-sct p-3"}>
        <div className={`auth-section mx-auto`}>
          <div
            className={`grid grid-cols-12 rounded-35 border border-purple-100 items-center overflow-auto cal-h shadow-shd-on mx-auto 
             bg-[#FFFFFF] max-w-auto`}
          >
            {/* welcome form */}
            <div className="sm:col-span-6 col-span-12 3xl:py-[60px] lg:px-[60px] lg:py-[30px] px-[30px] py-[30px] h-full flex flex-col justify-between">
              {
                isPalCareDev2Domain || isSeamedixDomain ? (
                  <>
                    {logoImage && (
                      <img
                        className="mx-auto lg:w-[150px] w-[100px] mb-[3px]"
                        src={logoImage}
                        alt="logo"
                      />
                    )}
                    {ImagePath?.seamedixsLogo && (
                      <img
                        className="mx-auto lg:w-[166px] w-[160px] mb-[21px] mt-[21px]"
                        src={ImagePath.seamedixsLogo}
                        alt="logo"
                      />
                    )}
                  </>
                )
                  :
                  isOnshipDomain ?
                    <img
                      className="mx-auto 3xl:w-[180px] 2xl:w-[180px] lg:w-[120px] w-[140px]"
                      src={logoImage}
                      alt="logo"
                    />
                    :
                    <img
                      style={isSeaDanceDomain ? { marginBottom: '0px' } : { marginBottom: "50px" }}
                      className="mx-auto lg:w-[250px] w-[300px]"
                      src={logoImage}
                      alt="logo"
                    />
              }
              <div>
                <>
                  {
                    showAuth && isLaptopScreen && isSeamedixDomain ?
                      <></>
                      :
                      isPalCareDev2Domain || isSeamedixDomain ?
                        <h1 className="tl-heading !mb-[24px]">Empowering You With<br />Connection. Care. Confidence.</h1>
                        :
                        (isOnshipDomain && isFrontmAiDomain) ?
                          <div className="mb-8">
                            <h1 className="font-semibold text-black text-[24px] text-center mb-1">FrontM Digital Toolbox</h1>
                            <p className="text-black text-[14px] text-center mb-4">One gateway for your maritime apps and data</p>
                          </div>
                          :
                          (isOnshipDomain && isStageOnshipDomain) ?
                            <h1 className="font-semibold text-black text-[30px] text-center mb-8">Welcome!</h1>
                            :
                            isOnshipDomain ?
                              <>
                                <h1 className="font-semibold text-black text-[30px] text-center">The Maritime Superapp</h1>
                                <p className="text-black text-[18px] text-center">Welcome to your maritime community,</p>
                                <p className="text-black text-[18px] text-center mb-4">services and fun, all in one place</p>
                              </>
                              :
                              isVikandDomain ?
                                <h1 className="text-[rgb(102,102,102)] text-[1.3rem] text-center font-normal mb-8">Login to VIKAND Connect</h1>
                                :
                                <>
                                  <div className="mb-8">
                                    <h1 className="tl-heading">Welcome!</h1>
                                    <h1 className="text-center text-size-14">Log in to {isSeaDanceDomain ? "Sea-Dance" : "Station Satcom"}</h1>
                                  </div>
                                </>
                  }
                </>
                <Card>
                  <div className="space-y-[17px]">
                    <div className="space-y-2 relative">
                      <Label
                        htmlFor="email"
                        labelError={errors.emailError}
                        highlightEmail={highlightEmail}
                        isFocused={isFocused.email}
                      >
                        Email
                      </Label>
                      <Input
                        type="email"
                        inputError={errors.emailError}
                        placeholder="Please enter your email address"
                        onChange={(t) => handleChange(t, "email")}
                        value={userData.email}
                        highlightEmail={highlightEmail}
                        onFocus={() => {
                          setHighlightEmail(false);
                          setIsFocused({ email: true })
                        }}
                        onBlur={() => setIsFocused({ email: false })}
                      />
                      {errors.emailError && (
                        <p className="text-red text-size-10 font-regular">
                          {errors.emailError}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2 relative">
                      <Label
                        htmlFor="email"
                        labelError={errors.passwordError}
                        isFocused={isFocused.password}
                      >
                        Password
                      </Label>
                      <Input
                        type={showPassword ? "text" : "password"}
                        placeholder="Please enter your password"
                        inputError={errors.passwordError}
                        showPassword={showPassword}
                        setShowPassword={setShowPassword}
                        onChange={(t) => handleChange(t, "password")}
                        value={userData.password}
                        onBlur={() => setIsFocused({ password: false })}
                        onFocus={() => {
                          setIsFocused({ password: true })
                          if (userData.email !== "") {
                            setHighlightEmail(true);
                          } else {
                            setHighlightEmail(false);
                          }
                        }}
                      />
                      {errors.passwordError && (
                        <p className="text-red text-size-10 font-regular">
                          {errors.passwordError}.
                        </p>
                      )}
                    </div>
                    {/* Remember me and Forgot password section */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2 cursor-pointer">
                        <Checkbox
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(!rememberMe)}
                        />
                        <span className={`text-dark-3 font-regular ${isOnshipDomain ? "text-size-14" : "text-size-11"}`}>
                          Remember me
                        </span>
                      </div>
                      <Link to="/forgot-password" className="text-[#376AFA] text-size-14 cursor-pointer">Forgot password?</Link>
                    </div>
                    <Button className="text-white" onClick={handleSubmit}>
                      Log In
                    </Button>
                    {showAuth && !isFrontmAiDomain &&
                      <>
                        <div className="flex items-center justify-center gap-3 text-gray-300 text-size-14 mx-5">
                          <span className="bg-devider w-full h-[1px]"></span>
                          or
                          <span className="bg-devider w-full h-[1px]"></span>
                        </div>
                        <GoogleSignInButton handleGoogleSignIn={handleGoogleSignIn} />
                        <p className="text-[#727484] text-size-14 font-medium text-center">
                          You don’t have an account? {" "}
                          <a

                            className="text-[#376AFA] text-size-14 font-semibold cursor-pointer"
                            onClick={() => navigate("/register")}
                          >
                            Sign Up
                          </a>
                        </p>
                      </>
                    }
                  </div>
                </Card>
              </div>
              <img
                className={`mx-auto w-full max-w-[260px] lg:w-[231px] mt-2`}
                src={ImagePath?.poweredText}
                alt="logo"
              />
            </div>
            {/* welcome Image */}
            <AuthRightPanel bgImage={bgImage} />
          </div>
        </div>
        {showModal && (
          <CommonModalPopUp
            size="sm"
            noHeader={false}
            hideCrosIcon={false}
            onClose={() => setShowModal(false)}
          >
            <Suspense
              fallback={
                <div className="flex justify-center items-center h-60">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
                </div>
              }
            >
              <SeamedixLoginError />
            </Suspense>
          </CommonModalPopUp>
        )}
      </section>
      {isLoading && <LoadingModal />}
    </>
  );
}
export default Login;
